```vue // filepath: c:\development\github\aplos\NCA-SaaS-UI\src\App.vue
<script setup lang="ts">
  import { computed, onMounted, ref, watch, nextTick } from 'vue'
  import { RouterView } from 'vue-router'
  import { useColorMode } from '@vueuse/core'
  import { theme } from 'ant-design-vue'
  import { Toaster } from '@/components/ui/sonner'
  import { useUser } from '@/composables/useUser'
  import LoadingScreen from './shared/components/LoadingScreen.vue'
  import ErrorViewerPanel from '@/components/ErrorViewer/ErrorViewerPanel.vue'

  const { initialize, is_global_admin } = useUser()
  const { system, store } = useColorMode()
  const mode = computed(() => (store.value === 'auto' ? system.value : store.value))
  const userInitialized = ref<boolean>(false)

  // helper to read a CSS custom property with fallback
  const cssVar = (name: string, fallback = '') => {
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name)
      return (v || '').trim() || fallback
    } catch (e) {
      return fallback
    }
  }

  const buildThemeConfig = (isDark: boolean) => {
    // read the CSS variables (these will reflect the values computed on the element
    // that has the .dark class if present)
    const border = cssVar('--border', '#e5e7eb')
    const card = cssVar('--card', '#f4f4f4')
    const cardLighter = cssVar('--card-lighter', card)
    const cardLight = cssVar('--card-light', card)
    const primary = cssVar('--primary', '#16a34a')
    const background = cssVar('--background', 'hsl(0, 0%, 100%)')
    const menuBarBorder = cssVar('--menu-bar-border', '#1e293b')

    return {
      token: {
        colorPrimary: primary,
        colorSuccess: '#5aa745',
        colorError: '#ff5757',
        colorFillQuaternary: cardLighter,
        colorBorder: border,
        colorBgLayout: background,
        colorBgContainer: '#ffffff',
        colorPrimaryBg: primary,
        ...(isDark && {
          colorPrimaryBg: '#0C101D',
          colorBgContainer: 'card',
          colorBorderSecondary: menuBarBorder,
          colorBorder: border,
          colorBgElevated: '#020817',
          colorFillQuaternary: cardLight,
          colorBgMask: '#000000c7'
        })
      },
      algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm
    }
  }

  const themeConfig = ref(buildThemeConfig(mode.value === 'dark'))

  watch(
    mode,
    async (newMode) => {
      // wait for DOM class toggles to be applied so getComputedStyle reads the correct CSS vars
      await nextTick()
      themeConfig.value = buildThemeConfig(newMode === 'dark')
    },
    { immediate: false }
  )

  onMounted(async () => {
    // fire-and-forget is fine; components will still await if needed
    initialize().catch(() => {})
    await nextTick()
    userInitialized.value = true
  })
</script>

<template>
  <LoadingScreen v-if="!userInitialized" :state="{ status: 'loading' }" />
  <div v-else class="flex flex-col">
    <a-config-provider :theme="themeConfig">
      <RouterView />
    </a-config-provider>
    <Toaster :theme="mode" rich-colors position="top-center" />
    <ErrorViewerPanel v-if="is_global_admin()" />
  </div>
</template>
```
